import posthog from 'posthog-js';
import { logger } from '../../utils/logger';

let isInitialized = false;

export interface PostHogConfig {
  host: string;
  apiKey: string;
  /** Reverse proxy URL to bypass ad blockers (e.g., https://analytics.dev.autar.ai) */
  proxyHost?: string;
}

export const initPostHog = (config?: PostHogConfig) => {
  // Prevent re-initialization
  if (isInitialized) {
    logger.warn('PostHog already initialized');
    return;
  }

  // Use runtime config if provided, fallback to VITE_* for local dev
  const directHost = config?.host || import.meta.env.VITE_POSTHOG_HOST;
  const apiKey =
    config?.apiKey || import.meta.env.VITE_POSTHOG_API_KEY || import.meta.env.VITE_POSTHOG_KEY;
  const proxyHost = config?.proxyHost;

  if (!apiKey || !directHost) {
    logger.warn('PostHog not configured - analytics disabled');
    return;
  }

  // Use reverse proxy if provided (bypasses ad blockers)
  const apiHost = proxyHost || directHost;

  posthog.init(apiKey, {
    api_host: apiHost,
    // Keep ui_host pointing to PostHog for dashboard links
    ui_host: directHost,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    persistence: 'localStorage',
    respect_dnt: true,
    disable_session_recording: false,
    // Start opted-out by default; restored from localStorage on return visits
    opt_out_capturing_by_default: true,
    // Enable debug output in development; suppress in production to avoid
    // styled %c console banners that delay the main thread on OAuth page reload
    debug: !import.meta.env.PROD,
  });

  isInitialized = true;
};

export const identifyUser = (userId: string, traits?: Record<string, unknown>) => {
  if (!isInitialized) return;
  posthog.identify(userId, traits);
};

const trackEvent = (event: string, properties?: Record<string, unknown>) => {
  if (!isInitialized) return;
  posthog.capture(event, properties);
};

const resetUser = () => {
  if (!isInitialized) return;
  posthog.reset();
};

export const optInPostHog = () => {
  if (!isInitialized) return;
  posthog.opt_in_capturing();
};

export const optOutPostHog = () => {
  if (!isInitialized) return;
  posthog.opt_out_capturing();
};

// Get distinct_id for backend correlation (anonymous or identified)
export const getDistinctId = (): string | undefined => {
  if (!isInitialized) return undefined;
  return posthog.get_distinct_id();
};

// Typed event helpers
export const analytics = {
  // ====== Analysis Events ======
  analysisStarted: (repoUrl: string, repoType: 'github' | 'gitlab' | 'local') => {
    trackEvent('analysis_started', { repo_url: repoUrl, repo_type: repoType });
  },
  analysisCompleted: (repoUrl: string, duration: number, agentCount?: number) => {
    trackEvent('analysis_completed', {
      repo_url: repoUrl,
      duration_ms: duration,
      agent_count: agentCount,
    });
  },
  analysisFailed: (repoUrl: string, error: string) => {
    trackEvent('analysis_failed', { repo_url: repoUrl, error });
  },

  /**
   * Track when user views analysis results (client-side).
   */
  analysisViewed: (
    analysisId: string,
    viewType: 'summary' | 'details' | 'recommendations' | 'full',
  ) => {
    trackEvent('analysis_viewed', {
      analysis_id: analysisId,
      view_type: viewType,
      source: 'client',
    });
  },

  // ====== Auth Events ======
  userSignedUp: (method: 'email' | 'github' | 'gitlab' | 'google') => {
    trackEvent('user_signed_up', { method });
  },
  userLoggedIn: (method: 'email' | 'github' | 'gitlab' | 'google') => {
    trackEvent('user_logged_in', { method });
  },
  userLoggedOut: () => {
    trackEvent('user_logged_out');
    resetUser();
  },

  // ====== Feature Usage ======
  featureUsed: (feature: string, metadata?: Record<string, unknown>) => {
    trackEvent('feature_used', { feature, ...metadata });
  },

  // ====== Repository Events ======
  repositorySelected: (repoName: string, repoType: 'github' | 'gitlab' | 'local') => {
    trackEvent('repository_selected', { repo_name: repoName, repo_type: repoType });
  },

  // ====== OAuth Events ======
  oauthConnected: (provider: 'github' | 'gitlab') => {
    trackEvent('oauth_connected', { provider });
  },
  oauthDisconnected: (provider: 'github' | 'gitlab') => {
    trackEvent('oauth_disconnected', { provider });
  },

  // ====== Navigation Events ======
  pageViewed: (pageName: string, metadata?: Record<string, unknown>) => {
    trackEvent('page_viewed', { page_name: pageName, ...metadata });
  },

  // ====== Product Metrics ======

  /**
   * Track when user clicks on a recommendation.
   */
  recommendationClicked: (data: {
    analysisId: string;
    recommendationType: string; // security, performance, maintenance, etc.
    recommendationId?: string;
    priority?: 'high' | 'medium' | 'low';
    action?: 'view_details' | 'apply_fix' | 'dismiss' | 'snooze';
  }) => {
    trackEvent('recommendation_clicked', {
      analysis_id: data.analysisId,
      recommendation_type: data.recommendationType,
      recommendation_id: data.recommendationId,
      priority: data.priority,
      action: data.action || 'view_details',
    });
  },

  /**
   * Track onboarding funnel progress (client-side).
   */
  onboardingStepCompleted: (data: { step: string; stepNumber: number; totalSteps: number }) => {
    trackEvent('onboarding_step_completed', {
      step: data.step,
      step_number: data.stepNumber,
      total_steps: data.totalSteps,
      source: 'client',
    });
  },

  /**
   * Track dashboard interactions.
   */
  dashboardInteraction: (data: {
    action:
      | 'filter_changed'
      | 'sort_changed'
      | 'export_clicked'
      | 'refresh_clicked'
      | 'card_expanded';
    section?: string; // which dashboard section
    filterType?: string;
    value?: string;
  }) => {
    trackEvent('dashboard_interaction', {
      action: data.action,
      section: data.section,
      filter_type: data.filterType,
      value: data.value,
    });
  },

  /**
   * Track file/code viewer interactions.
   */
  codeViewed: (data: {
    analysisId: string;
    filePath: string;
    language?: string;
    hasIssues: boolean;
    issueCount?: number;
  }) => {
    trackEvent('code_viewed', {
      analysis_id: data.analysisId,
      file_path: data.filePath,
      language: data.language,
      has_issues: data.hasIssues,
      issue_count: data.issueCount,
    });
  },

  /**
   * Track search usage.
   */
  searchPerformed: (data: {
    query: string;
    resultCount: number;
    searchType: 'global' | 'analysis' | 'recommendations' | 'files';
    hasResults: boolean;
  }) => {
    trackEvent('search_performed', {
      query_length: data.query.length, // Don't log actual query for privacy
      result_count: data.resultCount,
      search_type: data.searchType,
      has_results: data.hasResults,
    });
  },

  /**
   * Track error experiences.
   */
  errorDisplayed: (data: {
    errorType: string;
    errorCode?: string;
    context?: string; // page/component where error occurred
    recoverable: boolean;
  }) => {
    trackEvent('error_displayed', {
      error_type: data.errorType,
      error_code: data.errorCode,
      context: data.context,
      recoverable: data.recoverable,
    });
  },
};

export default posthog;
